import { InvoicePaymentDetails } from '../components/InvoicePaymentDetails';
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchClientPortalInvoice } from '../api/clientPortalApi';
import { ClientPortalLayout } from '../components/ClientPortalLayout';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { BackButton } from '../components/BackButton';
import { formatCurrency, formatDate } from '../utils/formatters';

export function ClientPortalInvoicePage() {
  const { "*": invoiceName } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['client-portal-invoice', invoiceName],
    queryFn: fetchClientPortalInvoice,
  });

  const invoice = data?.result;

  return (
    <ClientPortalLayout>
      <div className="container py-3">
        {isLoading ? (
          <div className="page-container text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading invoice details...</p>
          </div>
        ) : isError ? (
          <div className="page-container">
            <Alert variant="danger">{error.message}</Alert>
          </div>
        ) : !invoice ? (
          <div className="page-container">
            <Alert variant="warning">Invoice not found.</Alert>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <BackButton fallbackUrl="/client/invoices" />
              <a href={invoice.download_link} className="btn btn-primary" target="_blank" rel="noreferrer">
                Download PDF
              </a>
            </div>
            
            <div className="row mb-4">
              <div className="col-12">
                <h2>{invoice.name} <PaymentStatusBadge state={invoice.payment_state} /></h2>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4">
                <Card className="h-100">
                  <Card.Header as="h5">Invoice Details</Card.Header>
                  <Card.Body>
                    <p className="mb-1 text-muted small">Company</p>
                    <p className="fw-bold">{invoice.partner?.name || '-'}</p>
                    
                    <p className="mb-1 text-muted small">Invoice Date</p>
                    <p className="fw-bold">{formatDate(invoice.invoice_date)}</p>
                    
                    <p className="mb-1 text-muted small">Due Date</p>
                    <p className="fw-bold">{formatDate(invoice.invoice_date_due)}</p>

                    {invoice.invoice_origin !== "False" && (
                      <>
                        <p className="mb-1 text-muted small">Source Document</p>
                        <p className="fw-bold">
                          {invoice.invoice_origin && invoice.invoice_origin.startsWith('SO') ? (
                            <Link to={`/client/orders/${invoice.invoice_origin}`}>{invoice.invoice_origin}</Link>
                          ) : (
                            invoice.invoice_origin
                          )}
                        </p>
                      </>
                    )}

                    {invoice.ref && invoice.ref !== "False" && (
                       <>
                        <p className="mb-1 text-muted small">Reference</p>
                        <p className="fw-bold">{invoice.ref}</p>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </div>
              
              <div className="col-md-8">
                <Card className="h-100">
                  <Card.Header as="h5">Products</Card.Header>
                  <Table responsive striped className="card-body table mb-0">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="text-end">Quantity</th>
                        <th className="text-end">Unit Price</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoice.lines || []).map((line, idx) => (
                        <tr key={idx}>
                          <td>
                            {line.product?.printable_default_code && (
                              <div className="small text-muted">{line.product.printable_default_code}</div>
                            )}
                            <div>{line.name}</div>
                          </td>
                          <td className="text-end">{line.quantity}</td>
                          <td className="text-end">{formatCurrency(line.price_unit)}</td>
                          <td className="text-end">{formatCurrency(line.price_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-end fw-bold border-0">Total Amount</td>
                        <td className="text-end fw-bold border-0">{formatCurrency(invoice.amount_total_client_signed)}</td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="text-end fw-bold border-0">Amount Due</td>
                        <td className="text-end fw-bold border-0 text-danger">{formatCurrency(invoice.amount_residual_client_signed)}</td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card>
              </div>
            </div>
            <InvoicePaymentDetails invoiceName={invoice.name} inline />
          </>
        )}
      </div>
    </ClientPortalLayout>
  );
}
